import { Customer_BankAccounts, Customer_PaymentMethods, Customer_Sipos, Enums_BankType, Enums_PaymentMethodType } from "data/endpoints";
import IRepository from "data/IRepository";
import CustomerBankAccountDTO from "features/customer/dtos/CustomerBankAccountDTO";
import CustomerSipoAccountDTO from "features/customer/dtos/CustomerSipoAccountDTO";
import BankAccountDTO from "features/payments/dtos/BankAccountDTO";
import PaymentMethodCategoryDTO from "features/payments/dtos/PaymentMethodCategoryDTO";
import PaymentMethodDTO from "features/payments/dtos/PaymentMethodDTO";
import { Currency } from "features/shared/enums/currency";
import BankTypeEnumDTO from "features/shared/enums/dynamic/BankTypeEnumDTO";
import PaymentMethodTypeEnumDTO from "features/shared/enums/dynamic/PaymentMethodTypeEnumDTO";
import { FormFields, ValueActionType } from "features/shared/forms/hooks/useForm";
import { formatBankAccount } from "features/shared/helpers/Formatters";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { inversifyContainer } from "infrastructure/ioc/ioc.container";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { injectable } from "inversify";
import { paymentCategoriesMapping } from "features/requirements/sections/requirementFormTypes/paymentMethodForm/paymentCategoriesMapping";
import IPaymentMethodFormService from "./IPaymentMethodFormService";
import { Nullable } from "features/shared/types/nullable";

@injectable()
export default class PaymentMethodFormService implements IPaymentMethodFormService {
  private readonly _paymentMethodRepository: IRepository<PaymentMethodDTO>;
  private readonly _paymentMethodTypeRepository: IRepository<PaymentMethodTypeEnumDTO>;
  private readonly _bankAccountRepository: IRepository<CustomerBankAccountDTO>;
  private readonly _sipoRepository: IRepository<CustomerSipoAccountDTO>;
  private readonly _bankRepository: IRepository<BankTypeEnumDTO>;

  constructor() {
    this._paymentMethodRepository = inversifyContainer.get<IRepository<PaymentMethodDTO>>(IocTypes.Repository);
    this._paymentMethodTypeRepository = inversifyContainer.get<IRepository<PaymentMethodTypeEnumDTO>>(IocTypes.Repository);
    this._bankAccountRepository = inversifyContainer.get<IRepository<CustomerBankAccountDTO>>(IocTypes.Repository);
    this._sipoRepository = inversifyContainer.get<IRepository<CustomerSipoAccountDTO>>(IocTypes.Repository);
    this._bankRepository = inversifyContainer.get<IRepository<BankTypeEnumDTO>>(IocTypes.Repository);
  }

  fetchPaymentMethods(customerId: string, contractualAccountId: string, currency: Currency): Promise<PaymentMethodDTO[]> {
    return this._paymentMethodRepository.get({
      url: Customer_PaymentMethods,
      routeParams: getRouteParams("customerId", customerId),
      params: new URLSearchParams([
        ["ContractualAccountId", contractualAccountId],
        ["Currency", currency],
      ]),
    });
  }

  fetchPaymentMethodTypes(customerId: string, currency: Currency): Promise<PaymentMethodTypeEnumDTO[]> {
    return this._paymentMethodTypeRepository.get({
      url: Enums_PaymentMethodType,
      params: new URLSearchParams([
        ["CustomerId", customerId],
        ["Currency", currency],
      ]),
    });
  }

  fetchBankAccounts(customerId: string, currency: Currency): Promise<CustomerBankAccountDTO[]> {
    return this._bankAccountRepository.get({
      url: Customer_BankAccounts,
      routeParams: getRouteParams("customerId", customerId),
      params: getRouteParams("Currency", currency)
    });
  }

  fetchSipos(customerId: string, currency: Currency): Promise<CustomerSipoAccountDTO[]> {
    return this._sipoRepository.get({
      url: Customer_Sipos,
      routeParams: getRouteParams("customerId", customerId),
      params: getRouteParams("Currency", currency)
    });
  }

  fetchBanks(): Promise<BankTypeEnumDTO[]> {
    return this._bankRepository.get({
      url: Enums_BankType
    });
  }

  setPaymentMethodsDefaults(
    actualPaymentMethods: PaymentMethodDTO[],
    paymentMethodTypes: PaymentMethodTypeEnumDTO[],
    bankAccounts: CustomerBankAccountDTO[],
    sipos: CustomerSipoAccountDTO[],
    setFormValue: (key: string, value: any, action: ValueActionType) => void
  ): void {
    for (const actualPaymentMethod of actualPaymentMethods) {
      let paymentMethodType = paymentMethodTypes.find(x => x.code === actualPaymentMethod.paymentCategory);

      if (paymentMethodType && paymentMethodType.categories) {
        let defaultPaymentMethodCategory = paymentMethodType.categories.find(x => x.code === actualPaymentMethod.paymentMethodType);

        if (defaultPaymentMethodCategory) {
          // clear previous values
          this.clearPreviousBankAccount(actualPaymentMethod.paymentCategory!, setFormValue);
          this.clearPreviousSipoAccount(actualPaymentMethod.paymentCategory!, setFormValue);

          // set default values
          setFormValue(`${paymentCategoriesMapping[actualPaymentMethod.paymentCategory!]}Zpusob`, defaultPaymentMethodCategory.paymentMethodTypeId, "add");
          
          switch (defaultPaymentMethodCategory.accountType) {
            case "BankAccount":
            case "IBAN":
              this.setDefaultBankAccount(actualPaymentMethod, bankAccounts, setFormValue);
              break;
            case "Sipo":
              this.setDefaultSipo(actualPaymentMethod, sipos, setFormValue);
              break;
            default:
              break;
          }
        }
      }
    }
  }

  findSelectedValue<T extends string | object>(data: T[], values: FormFields, inputName: string, searchProperty?: string): Nullable<T> {
    let findValue: Nullable<T> = null;
    if (values[inputName]) {
      // object
      if (searchProperty !== undefined) {
        findValue = data.find((x) => (x as any)[searchProperty] === values[inputName]) ?? null;
      } 
      // primitive type
      else {
        findValue = data.find((x) => x === values[inputName]) ?? null;
      }
    }

    return findValue;
  }

  findSelectedBankAccount(accounts: CustomerBankAccountDTO[], values: FormFields, category: string): Nullable<CustomerBankAccountDTO> {
    let descPrefix = paymentCategoriesMapping[category];
    let originalAccount = this.findSelectedValue<CustomerBankAccountDTO>(accounts, values, `${descPrefix}BankUcet`, "accountNumberId");
    // existing bank account
    if (originalAccount) {
      return originalAccount;
    }
    // new bank account
    if (values[`${descPrefix}CisloUctu`]) {
      let bankAccount = {
        accountNumber: values[`${descPrefix}CisloUctu`],
        bankCode: values[`${descPrefix}KodBanky`],
        preAccountNumber: values[`${descPrefix}Predcisli`] ?? undefined
      };

      let newAccount = accounts.find(x => formatBankAccount(x) === formatBankAccount(bankAccount));

      return newAccount ?? null;
    }

    return null;
  }

  findSelectedSipo(accounts: CustomerSipoAccountDTO[], values: FormFields, category: string): Nullable<CustomerSipoAccountDTO> {
    let descPrefix = paymentCategoriesMapping[category];
    let originalAccount = this.findSelectedValue<CustomerSipoAccountDTO>(accounts, values, `${descPrefix}CisloSipo`, "sipoNumberId");
    // existing sipo account
    if (originalAccount) {
      return originalAccount;
    }
    // new sipo account
    if (values[`${descPrefix}SpojCislo`]) {
      let sipoNumber = values[`${descPrefix}SpojCislo`];

      let newSipo = accounts.find(x => x.sipoNumber === sipoNumber);

      return newSipo ?? null;
    }

    return null;
  }

  setNewBankAccount(bankAccount: CustomerBankAccountDTO, category: string, setFormValue: (key: string, value: any, action: ValueActionType) => void): void {
    let descPrefix = paymentCategoriesMapping[category];

    setFormValue(`${descPrefix}CisloUctu`, bankAccount.accountNumber, "add");
    setFormValue(`${descPrefix}KodBanky`, bankAccount.bankCode, "add");

    if (bankAccount.preAccountNumber) {
      setFormValue(`${descPrefix}Predcisli`, bankAccount.preAccountNumber, "add");
    }
    if (bankAccount.iban) {
      setFormValue(`${descPrefix}Iban`, bankAccount.iban, "add");
    }
  }

  clearPreviousBankAccount(category: string, setFormValue: (key: string, value: any, action: ValueActionType) => void): void {
    let descPrefix = paymentCategoriesMapping[category]; 
    let descriptors = ["CisloUctu", "KodBanky", "Predcisli", "Iban"];

    for (const desc of descriptors) {
      setFormValue(`${descPrefix}${desc}`, null, "remove");
    }

    setFormValue(`${descPrefix}BankUcet`, null, "remove");
  }

  clearPreviousSipoAccount(category: string, setFormValue: (key: string, value: any, action: ValueActionType) => void): void {
    let descPrefix = paymentCategoriesMapping[category]; 
    
    setFormValue(`${descPrefix}SpojCislo`, null, "remove"); // new sipo
    setFormValue(`${descPrefix}CisloSipo`, null, "remove"); // existing sipo
  }

  private setDefaultBankAccount(
    paymentMethod: PaymentMethodDTO,
    bankAccounts: CustomerBankAccountDTO[],
    setFormValue: (key: string, value: any, action: ValueActionType) => void
  ): void {
    if (paymentMethod.bankAccount) {
      let accountStr = formatBankAccount(paymentMethod.bankAccount);
      let defaultBankAccount = bankAccounts.find(x => formatBankAccount(x) === accountStr);
      
      if (defaultBankAccount) {
        setFormValue(`${paymentCategoriesMapping[paymentMethod.paymentCategory!]}BankUcet`, defaultBankAccount.accountNumberId, "add");
      }
    }
  }

  private setDefaultSipo(
    paymentMethod: PaymentMethodDTO,
    sipos: CustomerSipoAccountDTO[],
    setFormValue: (key: string, value: any, action: ValueActionType) => void
  ): void {
    if (paymentMethod.sipoAccount) {
      let defaultSipo = sipos.find(sipo => sipo.sipoNumber === paymentMethod.sipoAccount?.sipoNumber);
      
      if (defaultSipo) {
        setFormValue(`${paymentCategoriesMapping[paymentMethod.paymentCategory!]}CisloSipo`, defaultSipo.sipoNumberId, "add");
      }
    }
  }
}